import React, { useState, useEffect } from 'react';
import '../css/CurrentCars.css';
import '../css/HighwayTicketButton.css';
import {FaEye, FaCheckCircle, FaTimesCircle, FaChevronRight, FaRoad } from 'react-icons/fa';
import CarDropdown from '../modal/CarDropdown';
import { vehiclesAPI, authAPI, highwayTicketAPI } from '../api/api';
import { usePermissions } from '../hooks/usePermissions';

const allFields = [
  { key: 'chassis_number', label: 'Alvázszám' },
  { key: 'plate_number', label: 'Rendszám' },
  { key: 'handover', label: 'Átadás dátum' },
  { key: 'handoff', label: 'Leadás dátum' },
  { key: 'monthly_distance', label: 'Futás' },
  { key: 'rentalFee', label: 'Bérköltség' },
  { key: 'model', label: 'Modell' },
  { key: 'brand', label: 'Márka' },
];

function FilterModal({ open, onClose, fields, visibleFields, onChange }) {
  if (!open) return null;
  const handleCheckboxChange = (key) => {
    if (visibleFields.includes(key)) {
      onChange(key);
    } else if (visibleFields.length < 5) {
      onChange(key);
    }
  };
  return (
    <div className="field-filter-modal-backdrop" onClick={onClose}>
      <div className="field-filter-modal-content" onClick={e => e.stopPropagation()}>
        <h3>Mezők szűrése</h3>
        <div style={{ color: 'red', fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>
          Maximum 5 mező választható ki egyszerre!
        </div>
        <form>
          {fields.map(field => (
            <div key={field.key}>
              <label>
                <input
                  type="checkbox"
                  checked={visibleFields.includes(field.key)}
                  onChange={() => handleCheckboxChange(field.key)}
                  disabled={!visibleFields.includes(field.key) && visibleFields.length >= 6}
                />
                {field.label}
              </label>
            </div>
          ))}
        </form>
        <button onClick={onClose} className="field-filter-modal-close-btn">Bezár</button>
      </div>
    </div>
  );
}

function CarRow({ car, visibleFields, isOpen, onDropdown, onHighwayTicketRequest, requestingTicket }) {
  let progress = 0;
  let progressColor = 'green';
  let statusLabel = '';
  let statusText = 'Használatban';
  let dynamicStatus = car.status_info?.status || car.status;

  // Calculate progress based on km_data from vehicle_rental and trip tables
  if (car.km_data && dynamicStatus !== 'order') {
    const kmDriven = car.km_data.km_driven || 0;
    const kmLimit = car.km_data.km_limit_monthly || 1;

    // Calculate percentage: (driven / limit) * 100
    progress = Math.max(0, Math.min(100, Math.round((kmDriven / kmLimit) * 100)));

    // Set color and status based on progress
    if (progress >= 100) {
      // Overage - red
      progressColor = 'red';
      dynamicStatus = 'error';
      statusLabel = 'Túlfutás';
      statusText = 'Túlfutás';
    } else if (progress >= 90) {
      // Warning - orange/yellow
      progressColor = 'orange';
      dynamicStatus = 'info';
      statusLabel = 'Közel a limithez';
      statusText = 'Figyelmeztetés';
    } else if (progress >= 75) {
      // Caution - blue
      progressColor = 'blue';
      dynamicStatus = 'info';
      statusLabel = 'Háromnegyedes használat';
      statusText = 'Információ';
    } else {
      // Normal - green
      progressColor = 'green';
      dynamicStatus = 'ok';
      statusLabel = '';
      statusText = 'Használatban';
    }
  } else if (dynamicStatus === 'order') {
    // Order status - keep original logic
    progress = car.progress || 0;
    progressColor = car.progressColor || 'blue';
    statusLabel = car.statusLabel || '';
    statusText = car.statusText || 'Rendelve';
  }


  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(isOpen);
  useEffect(() => {
    if (isOpen) {
      setShouldRenderDropdown(true);
    } else if (shouldRenderDropdown) {
      const timeout = setTimeout(() => setShouldRenderDropdown(false), 450);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, shouldRenderDropdown]);

  return (
    <>
      <div className="car-card" style={{display:'flex',flexDirection:'row',position:'relative'}}>
        <div className="car-card-imgwrap">
          <img src={car.gallery && car.gallery[0]} alt={car.model} className="car-card-img" />
        </div>
        <div style={{flex:1,display:'flex',flexDirection:'column'}}>
          <div className="car-card-row1">
            {visibleFields.includes('chassis_number') && (
              <div className="car-card-cell vin" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Alvázszám</span>
                {car.chassis_number17}
              </div>
            )}
            {visibleFields.includes('plate_number') && (
              <div className="car-card-cell plate" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Rendszám</span>
                {car.plate_number}
              </div>
            )}
            {visibleFields.includes('handover') && (
              <div className="car-card-cell handover" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Átadás dátum</span>
                {car.handover}
              </div>
            )}
            {visibleFields.includes('handoff') && (
              <div className="car-card-cell handoff" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Leadás dátum</span>
                {car.handoff}
              </div>
            )}
            {visibleFields.includes('monthly_distance') && (
              <div className="car-card-cell futas" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Futás</span>
                {car.monthly_distance}
              </div>
            )}
            {visibleFields.includes('rentalFee') && (
              <div className="car-card-cell rentalFee" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Bérköltség</span>
                {car.rentalFee}
              </div>
            )}
            {visibleFields.includes('model') && (
              <div className="car-card-cell model" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Modell</span>
                {car.model}
              </div>
            )}
            {visibleFields.includes('brand') && (
              <div className="car-card-cell brand" style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <span className='cell_span'>Márka</span>
                {car.brand}
              </div>
            )}
          </div>
          <div className="car-row-divider" style={{marginLeft:0,width:'100%'}}></div>
          <div className="car-card-row2">
            <div className="car-card-badges">
              <span className="car-card-badge owner">{car.owner}</span>
              <span className="car-card-badge highway">Autópálya matrica: {car.highway_ticket}</span>
              {(!car.highway_ticket || car.highway_ticket === '---' || car.highway_ticket === 'Nincs' || car.highway_ticket === '') && (
                <button
                  className="highway-ticket-request-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onHighwayTicketRequest(car.plate_number);
                  }}
                  title="Autópálya matrica igénylése"
                  disabled={requestingTicket === car.plate_number}
                >
                  <FaRoad /> {requestingTicket === car.plate_number ? 'Igénylés...' : 'Matrica igénylés'}
                </button>
              )}
            </div>
            <div className="car-card-progresswrap">
              <div className="car-card-progress-bg">
                <div className={`car-card-progress-bar car-card-progress-bar-${progressColor}`} style={{ width: `${progress}%` }}></div>
              </div>
              <div className="car-card-status">
                {dynamicStatus === 'ok' && <FaCheckCircle className="car-status-ok status" />}
                {dynamicStatus === 'error' && <FaTimesCircle className="car-status-error status" />}
              </div>
              <span className="car-card-progress-label">{car.progressLabel || ''}</span>
            </div>
          </div>
        </div>
        <div className="car-card-dropdownicon" style={{position:'absolute',right:5, top:5}} onClick={() => onDropdown(car.chassis_number)}>
          <FaChevronRight className="car-dropdown-icon" style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }} />
        </div>
      </div>
      <div className={`car-dropdown-anim-wrapper${isOpen ? ' open' : ''}`}>{shouldRenderDropdown && <CarDropdown car={car} />}</div>
    </>
  );
}

const CurrentCars = ({ search, sort }) => {
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const { permissions, loading: permissionsLoading } = usePermissions();

  const [visibleFields, setVisibleFields] = useState([]);
  const [cars, setCars] = useState([]);
  const [archivedCars, setArchivedCars] = useState([]);
  const [openDropdowns, setOpenDropdowns] = useState([]); // chassis_number tömb
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [requestingTicket, setRequestingTicket] = useState(null);

  const user = authAPI.getCurrentUser();

  // Initialize visible fields when permissions are loaded
  useEffect(() => {
    // Wait until permissions are fully loaded
    if (permissionsLoading) {
      return;
    }

    // Build available fields based on permissions
    const availableFields = allFields.filter(field => {
      if (field.key === 'rentalFee') {
        return permissions?.can_see_rental_fee;
      }
      return true;
    });

    // Try to load saved visible fields from localStorage
    const savedFields = localStorage.getItem('currentCars_visibleFields');

    if (savedFields) {
      try {
        const parsed = JSON.parse(savedFields);

        // Filter to only valid fields
        let validFields = parsed.filter(key => availableFields.some(f => f.key === key));

        // If user has rentalFee permission and it's not in saved fields, add it
        if (permissions?.can_see_rental_fee && !validFields.includes('rentalFee')) {
          validFields.push('rentalFee');
        }

        // Ensure we have maximum 6 fields
        if (validFields.length > 6) {
          validFields = validFields.slice(0, 6);
        }

        if (validFields.length > 0) {
          setVisibleFields(validFields);
          return;
        }
      } catch (e) {
        console.error('Error loading saved fields:', e);
      }
    }

    // Default: show specific fields
    const defaultFields = [
      ...(permissions?.can_see_rental_fee ? ['rentalFee'] : []),
      'model',
      'brand',
      'plate_number',
      'chassis_number',
    ].slice(0, 6); // Max 6 fields

    setVisibleFields(defaultFields);
  }, [permissionsLoading, permissions?.can_see_rental_fee]);

  // Save visible fields to localStorage whenever they change
  useEffect(() => {
    if (!permissionsLoading && visibleFields.length > 0) {
      localStorage.setItem('currentCars_visibleFields', JSON.stringify(visibleFields));
    }
  }, [visibleFields, permissionsLoading]);

  const handleHighwayTicketRequest = async (plateNumber) => {
    if (requestingTicket === plateNumber) return;

    setRequestingTicket(plateNumber);

    try {
      await highwayTicketAPI.requestTicket(plateNumber);
      alert('Autópálya matrica igénylése sikeresen elküldve!');

      // Update the car's highway_ticket locally to hide the button immediately
      setCars(prevCars => prevCars.map(car =>
        car.plate_number === plateNumber
          ? { ...car, highway_ticket: 'Igényelve' }
          : car
      ));
    } catch (error) {
      console.error('Highway ticket request error:', error);
      alert('Hiba történt az autópálya matrica igénylésekor: ' + error.message);
    } finally {
      setRequestingTicket(null);
    }
  };

  useEffect(() => {
    const loadCars = async () => {
      try {

        if (!user) {
          console.warn('Nincs bejelentkezett felhasználó');
          setCars([]);
          return;
        }

        const carsData = await vehiclesAPI.getAllVehiclesForUser();
        setCars(carsData);
      } catch (error) {
        console.error('Hiba a járművek betöltésekor:', error);

        setCars([]);
      }
    };

    loadCars();

    // Refresh cars data every minute to update latest positions
    const intervalId = setInterval(() => {
      loadCars();
    }, 60000); // 60 seconds

    return () => clearInterval(intervalId);
  }, [user?.id]);

  useEffect(() => {
    document.body.classList.add('cars-bg');
    return () => document.body.classList.remove('cars-bg');
  }, []);


  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
        Kérjük, jelentkezzen be a járművek megtekintéséhez.
      </div>
    );
  }

  let carsToShow = cars.filter(car => car.status_info?.status !== 'order');
  if (search) {
    const s = search.toLowerCase();
    carsToShow = carsToShow.filter(car =>
      (car.chassis_number && car.chassis_number.toLowerCase().includes(s)) ||
      (car.chassis_number17 && car.chassis_number17.toLowerCase().includes(s)) ||
      (car.plate_number && car.plate_number.toLowerCase().includes(s)) ||
      (car.model && car.model.toLowerCase().includes(s)) ||
      (car.brand && car.brand.toLowerCase().includes(s)) ||
      (car.owner && car.owner.toLowerCase().includes(s))
    );
  }
  if (sortField) {
    carsToShow = [...carsToShow].sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (sortField === 'monthly_distance') {
        aVal = parseInt((aVal || '').replace(/[^0-9]/g, '')) || 0;
        bVal = parseInt((bVal || '').replace(/[^0-9]/g, '')) || 0;
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const handleFieldToggle = (key) => {
    setVisibleFields(prev =>
      prev.includes(key)
        ? prev.filter(f => f !== key)
        : [...prev, key]
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(dir => dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter fields based on permissions
  const availableFields = allFields.filter(field => {
    if (field.key === 'rentalFee') {
      return permissions?.can_see_rental_fee;
    }
    return true;
  });

  return (
    <>
      <div className="home-table-header-row">
        <div className="home-table-header" style={{ cursor: 'pointer' }} onClick={() => setFilterModalOpen(true)}>
          <FaEye />
        </div>
        {availableFields.map((field) => (
          visibleFields.includes(field.key) && (
            <div
              className="home-table-header-cell"
              key={field.key}
              onClick={() => handleSort(field.key)}
              style={{ cursor: 'pointer', userSelect: 'none', fontWeight: sortField === field.key ? 'bold' : undefined }}
            >
              {field.label}
              {sortField === field.key && (
                <span style={{ marginLeft: 4 }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
              )}
            </div>
          )
        ))}

      </div>
      {carsToShow.map((car, idx) => (
        <CarRow
          key={idx}
          car={car}
          visibleFields={visibleFields}
          isOpen={openDropdowns.includes(car.chassis_number)}
          onDropdown={chassis_number => setOpenDropdowns(openDropdowns => openDropdowns.includes(chassis_number) ? openDropdowns.filter(v => v !== chassis_number) : [...openDropdowns, chassis_number])}
          onHighwayTicketRequest={handleHighwayTicketRequest}
          requestingTicket={requestingTicket}
        />
      ))}
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fields={availableFields}
        visibleFields={visibleFields}
        onChange={handleFieldToggle}
      />
    </>
  );
};

export default CurrentCars;